import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import { appWithTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { Toaster } from "react-hot-toast";
import MainLayout from "Layout";
import ReactQuery from "ReactQuery";
import PageLoader from "components/PageLoader";
// Styles
import createEmotionCache from "styles/createEmotionCache";
import customTheme from "styles/jss/Theme.styles";
import globalStyle from "styles/jss/Global.styles";
import "styles/scss/main.scss";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
// Gallery lightBox
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
// Import styles pdf-viewer-reactjs
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
// Google Analytics
import Script from "next/script";
import * as gtag from "services/gtag";
// Captcha
import GoogleRecaptcha from "services/GoogleRecaptcha";
import { RecaptchaProvider } from "../Hook/recaptchaContext";
// Client-side cache, shared for the whole session of the user in the browser.
var ref = _sliced_to_array(createEmotionCache(), 2), cacheRtl = ref[0], cacheLtr = ref[1];
function MyApp(props) {
    var locale = useRouter().locale;
    var Component = props.Component, _emotionCache = props.emotionCache, emotionCache = _emotionCache === void 0 ? locale === "fa" ? cacheRtl : cacheLtr : _emotionCache, pageProps = props.pageProps, router = props.router;
    useEffect(function() {
        document.body.dir = locale === "fa" ? "rtl" : "ltr";
    }, [
        locale
    ]);
    var theme = useMemo(function() {
        return createTheme(_object_spread_props(_object_spread({}, customTheme), {
            typography: {
                fontFamily: locale === "fa" ? "'IRANYekan','Open Sans', Roboto, sans-serif  !important" : "Inter, 'Open Sans', Roboto, sans-serif  !important"
            }
        }));
    }, [
        locale
    ]);
    // blow useState & useEffect for tracking change route
    var ref = useState(false), isRouteChanging = ref[0], setIsRouteChanging = ref[1];
    useEffect(function() {
        //  Google Analytics
        var handleRouteChange = function(url) {
            return "object" !== "undefined" && window.location.origin === "https://www.aryasasol.com" && gtag.pageview(url);
        };
        // Loading Between Pages
        var routeChangeStartHandler = function() {
            return setIsRouteChanging(true);
        };
        var routeChangeEndHandler = function() {
            return setIsRouteChanging(false);
        };
        router.events.on("routeChangeStart", routeChangeStartHandler);
        router.events.on("routeChangeComplete", function() {
            routeChangeEndHandler();
            handleRouteChange();
        });
        router.events.on("routeChangeError", routeChangeEndHandler);
        return function() {
            router.events.off("routeChangeStart", routeChangeStartHandler);
            router.events.off("routeChangeComplete", function() {
                routeChangeEndHandler();
                handleRouteChange();
            });
            router.events.off("routeChangeError", routeChangeEndHandler);
        };
    }, [
        router.events
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Head, {
                children: /*#__PURE__*/ _jsx("meta", {
                    name: "viewport",
                    content: "width=device-width, initial-scale=1.0"
                })
            }),
            "object" !== "undefined" && window.location.origin === "https://www.aryasasol.com" ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Script, {
                        id: "google-analytics",
                        dangerouslySetInnerHTML: {
                            __html: "\n              window.dataLayer = window.dataLayer || [];\n              function gtag(){dataLayer.push(arguments);}\n              gtag('js', new Date());\n              gtag('config', 'G-212LW28ZKX', {\n                page_path: window.location.pathname,\n              });\n            "
                        }
                    }),
                    /*#__PURE__*/ _jsx(Script, {
                        strategy: "afterInteractive",
                        src: "https://www.googletagmanager.com/gtag/js?id=G-212LW28ZKX"
                    })
                ]
            }) : "",
            /*#__PURE__*/ _jsx(CacheProvider, {
                value: emotionCache,
                children: /*#__PURE__*/ _jsxs(ThemeProvider, {
                    theme: theme,
                    children: [
                        /*#__PURE__*/ _jsx(Toaster, {
                            toastOptions: {
                                duration: 5000,
                                success: {
                                    style: {
                                        border: "1px solid #007132",
                                        padding: "16px",
                                        color: "#007132"
                                    }
                                },
                                error: {
                                    style: {
                                        border: "1px solid #713200",
                                        padding: "16px",
                                        color: "#713200"
                                    }
                                }
                            }
                        }),
                        /*#__PURE__*/ _jsx(CssBaseline, {}),
                        /*#__PURE__*/ _jsx(GlobalStyles, {
                            styles: globalStyle
                        }),
                        /*#__PURE__*/ _jsx(ReactQuery, {
                            dehydratedState: pageProps.dehydratedState,
                            children: isRouteChanging ? /*#__PURE__*/ _jsx(PageLoader, {}) : /*#__PURE__*/ _jsx(GoogleRecaptcha, {
                                children: /*#__PURE__*/ _jsx(RecaptchaProvider, {
                                    children: /*#__PURE__*/ _jsx(MainLayout, {
                                        children: /*#__PURE__*/ _jsx(Component, _object_spread({}, pageProps))
                                    })
                                })
                            })
                        })
                    ]
                })
            })
        ]
    });
}
export default appWithTranslation(MyApp);
